/* eslint-disable no-fallthrough */
import { Breakpoints } from '../constants';
import getWindowBreakpoint from './getWindowBreakpoint';
import useResize from './useResize';

/**
 *
 * returns passed-in values as the window size changes
 *
 * A breakpoint key's correspoinding value will be returned if it is the largest breakpoint that is <= the current window breakpoint.
 * When the current window breakpoint is less than the smallest passed-in breakpoint key, this function will return undefined.
 *
 * see unit tests for more examples
 *
 * @example
 *
 * const buttonSize = useValueForBreakpoint({
 *     [Breakpoints.xxsmall]: ButtonSize.small,
 *     [Breakpoints.medium]: ButtonSize.large,
 * });
 *
 * // if window breakpoint < Breakpoints.xxsmall
 * //     buttonSize == undefined
 * // if Breakpoints.xxsmall <= window breakpoint < Breakpoints.medium
 * //     buttonSize == ButtonSize.small
 * // if window breakpoint >= Breakpoints.medium
 * //     buttonSize == ButtonSize.large
 *
 *
 * @param {Object.<number, *>} breakpointToValueDict
 * @returns value who's key is closest to and smaller-than-or-equal-to the current window breakpoint
 */
export default function useValueForBreakpoint({
    [Breakpoints.xxsmall]: xxsmall,
    [Breakpoints.xsmall]: xsmall,
    [Breakpoints.small]: small,
    [Breakpoints.medium]: medium,
    [Breakpoints.large]: large,
    [Breakpoints.xlarge]: xlarge,
    [Breakpoints.xxlarge]: xxlarge,
}) {
    return useResize(() =>
        getValueForBreakpoint({
            [Breakpoints.xxsmall]: xxsmall,
            [Breakpoints.xsmall]: xsmall,
            [Breakpoints.small]: small,
            [Breakpoints.medium]: medium,
            [Breakpoints.large]: large,
            [Breakpoints.xlarge]: xlarge,
            [Breakpoints.xxlarge]: xxlarge,
        })
    );
}

function getValueForBreakpoint({
    [Breakpoints.xxsmall]: xxsmall,
    [Breakpoints.xsmall]: xsmall,
    [Breakpoints.small]: small,
    [Breakpoints.medium]: medium,
    [Breakpoints.large]: large,
    [Breakpoints.xlarge]: xlarge,
    [Breakpoints.xxlarge]: xxlarge,
}) {
    const currBreakpoint = getWindowBreakpoint();

    switch (currBreakpoint) {
        case Breakpoints.xxlarge:
            if (xxlarge) return xxlarge;
        case Breakpoints.xlarge:
            if (xlarge) return xlarge;
        case Breakpoints.large:
            if (large) return large;
        case Breakpoints.medium:
            if (medium) return medium;
        case Breakpoints.small:
            if (small) return small;
        case Breakpoints.xsmall:
            if (xsmall) return xsmall;
        case Breakpoints.xxsmall:
            if (xxsmall) return xxsmall;
        default:
            return undefined;
    }
}
